
import { useState, type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cx } from '@client/shared/lib/cx.ts';
import styles from './Field.module.css';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  mono?: boolean;
}

export function Input({ mono = false, className, type, ...rest }: InputProps) {
  if (type === 'password') {
    return <PasswordInput mono={mono} className={className} {...rest} />;
  }
  return <input className={cx(styles.control, mono && styles.mono, className)} type={type} {...rest} />;
}

function PasswordInput({ mono, className, ...rest }: Omit<InputProps, 'type'>) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className={styles.passwordWrap}>
      <input
        className={cx(styles.control, styles.hasReveal, mono && styles.mono, className)}
        type={revealed ? 'text' : 'password'}
        {...rest}
      />
      <button
        type="button"
        tabIndex={-1}
        className={styles.revealToggle}
        aria-label={revealed ? 'Hide password' : 'Show password'}
        aria-pressed={revealed}
        onClick={() => setRevealed((v) => !v)}
      >
        <EyeIcon open={revealed} />
      </button>
    </div>
  );
}

function EyeIcon({ open }: { open: boolean }) {
  return (
    <svg width="17" height="17" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M1.2 8S3.7 3.4 8 3.4 14.8 8 14.8 8 12.3 12.6 8 12.6 1.2 8 1.2 8Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.3" />
      {!open && <path d="M2.5 2.5l11 11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />}
    </svg>
  );
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  mono?: boolean;
}

export function Textarea({ mono = false, className, ...rest }: TextareaProps) {
  return (
    <textarea
      className={cx(styles.control, styles.textarea, mono && styles.mono, className)}
      {...rest}
    />
  );
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  mono?: boolean;
}

export function Select({ mono = false, className, children, ...rest }: SelectProps) {
  return (
    <select className={cx(styles.control, styles.select, mono && styles.mono, className)} {...rest}>
      {children}
    </select>
  );
}
